import { useEffect, useState } from "react";

export interface StakeFlowEntranceState {
    /** Border trace IN should render. Active from t=0 until the
     *  border-out start. */
    borderIn: boolean;
    /** Border trace OUT should render. Active from border-out start
     *  until the unmount delay. */
    borderOut: boolean;
    /** Connector line drop-IN should render. Active from line-in
     *  start until line-out start. */
    lineIn: boolean;
    /** Connector line retract-OUT should render. Active from
     *  line-out start until border-out start. */
    lineOut: boolean;
    /** Receiver-card bump animation is active. Pulses true → false
     *  for `bumpDurationMs` once after the line lands. */
    bump: boolean;
    /** When true, the count-up should be running. Flipped on at
     *  bump-time and stays true thereafter. */
    countEnabled: boolean;
}

export interface UseStakeFlowEntranceOptions {
    /** Total duration of the border IN trace, in ms. Should match
     *  the BorderAnimation's effective IN total (sum of `topSpeed`
     *  + `sideSpeed` + `bottomSpeed`, defaults 200/200/200 = 600).
     *  Pass 700 if the caller overrides `sideSpeed` to 300 like
     *  YaasEarnProjectionCTA does. Default 700. */
    borderInTotalMs?: number;
    /** Connector-line drop-in duration, ms. Default 75. */
    lineInDurationMs?: number;
    /** Receiver-card bump duration, ms. Default 200. */
    bumpDurationMs?: number;
    /** Connector-line retract-out duration, ms. Default 200. */
    lineOutDurationMs?: number;
    /** Total duration of the border OUT trace, ms. Default 700. */
    borderOutTotalMs?: number;
}

const DEFAULTS: Required<UseStakeFlowEntranceOptions> = {
    borderInTotalMs: 700,
    lineInDurationMs: 75,
    bumpDurationMs: 200,
    lineOutDurationMs: 200,
    borderOutTotalMs: 700
};

/**
 * Stake-flow entrance choreography — the "border traces in around
 * Card A → connector line drops to Card B → Card B bumps + count-up
 * starts → connector line retracts → border traces out" sequence
 * shared by USDv stake's input flow and YaaS retail's
 * earnings-projection preview.
 *
 * The hook owns the timer chain only. Callers compose
 * `<BorderAnimation>` / `<ConnectorLine>` / `<BumpAnimation>` /
 * `useCountUp` themselves and read the returned booleans to drive
 * each primitive's mount / direction / triggered state.
 *
 * Plays once on mount. Cleanup cancels any pending timers if the
 * caller unmounts mid-sequence.
 *
 * Timeline (with defaults):
 *   t=0     borderIn = true                    — border IN traces
 *   t=700   lineIn = true                      — connector drops
 *   t=775   bump = true, countEnabled = true   — bump pulses, count-up
 *   t=975   bump = false, lineIn = false,
 *           lineOut = true                     — line retracts
 *   t=1175  lineOut = false, borderIn = false,
 *           borderOut = true                   — border OUT traces
 *   t=1875  borderOut = false                  — sequence done
 */
export function useStakeFlowEntrance(options: UseStakeFlowEntranceOptions = {}): StakeFlowEntranceState {
    const {
        borderInTotalMs = DEFAULTS.borderInTotalMs,
        lineInDurationMs = DEFAULTS.lineInDurationMs,
        bumpDurationMs = DEFAULTS.bumpDurationMs,
        lineOutDurationMs = DEFAULTS.lineOutDurationMs,
        borderOutTotalMs = DEFAULTS.borderOutTotalMs
    } = options;

    const [borderIn, setBorderIn] = useState(false);
    const [borderOut, setBorderOut] = useState(false);
    const [lineIn, setLineIn] = useState(false);
    const [lineOut, setLineOut] = useState(false);
    const [bump, setBump] = useState(false);
    const [countEnabled, setCountEnabled] = useState(false);

    useEffect(() => {
        // Each step starts after the previous step's animation
        // completes. Compute offsets from the duration options so
        // overrides slide the schedule cleanly.
        const lineInAt = borderInTotalMs;
        const bumpAt = lineInAt + lineInDurationMs;
        const lineOutAt = bumpAt + bumpDurationMs;
        const borderOutAt = lineOutAt + lineOutDurationMs;
        const unmountAt = borderOutAt + borderOutTotalMs;

        const timers: ReturnType<typeof setTimeout>[] = [];
        setBorderIn(true);
        timers.push(setTimeout(() => setLineIn(true), lineInAt));
        timers.push(
            setTimeout(() => {
                setBump(true);
                setCountEnabled(true);
            }, bumpAt)
        );
        timers.push(
            setTimeout(() => {
                setBump(false);
                setLineIn(false);
                setLineOut(true);
            }, lineOutAt)
        );
        timers.push(
            setTimeout(() => {
                setLineOut(false);
                setBorderIn(false);
                setBorderOut(true);
            }, borderOutAt)
        );
        timers.push(
            setTimeout(() => {
                setBorderOut(false);
            }, unmountAt)
        );
        return (): void => timers.forEach(clearTimeout);
        // The schedule is captured once on mount per the design
        // ("plays once"). If callers ever want to retrigger it on a
        // dependency change, that's a different hook signature
        // (replay-on-key); leave that out until a real consumer
        // needs it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        borderIn,
        borderOut,
        lineIn,
        lineOut,
        bump,
        countEnabled
    };
}
